import { Box, ScrollArea, Stack, Group, Badge, Text, Button, ActionIcon, Menu, Grid, Tabs, Alert, Loader, Center, Paper, Modal, Select, Radio, Container } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { IconArrowLeft, IconExternalLink, IconDots, IconRefresh, IconCheck, IconAlertCircle, IconCopy, IconDownload, IconFileText, IconChecklist, IconBraces, IconChevronDown, IconEdit, IconPlus } from '@tabler/icons-react';
import JobList from '../components/jobs/JobList';
import { Job, useRefreshJob, useGenerateAssessment, useUpdateJob, useScrapeJob, useJobs } from '../api/jobs';
import { useResumeContent } from '../api/resumes';
import { useArtifactsByJob } from '../api/artifacts';
import { useProviders, useGenerationStatus } from '../api/llm';
import UnifiedArtifactCompiler from '../components/shared/UnifiedArtifactCompiler';
import MilkdownRenderer from '../components/shared/MilkdownRenderer';
import JobResumesTab from '../components/jobs/JobResumesTab';
import AddJobModal from '../components/jobs/AddJobModal';
import EditDescriptionModal from '../components/jobs/EditDescriptionModal';
import { SubmissionStatusBanner } from '../components/jobs/SubmissionStatusBanner';
import { PageHeader } from '../components/common/PageHeader';
import { useUIStore } from '../store/ui.store';

const statusColors: Record<string, string> = {
  active: 'yellow',
  expired: 'gray',
  applied: 'green',
  rejected: 'red',
};

const statusLabels: Record<string, string> = {
  active: 'Active',
  expired: 'Expired',
  applied: 'Applied',
  rejected: 'Rejected',
};

// Helper function to trigger file download
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Helper function to extract and parse YAML frontmatter
function extractFrontmatter(markdown: string): { frontmatter: Record<string, any> | null; content: string } {
  const yamlMatch = markdown.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!yamlMatch) {
    return { frontmatter: null, content: markdown };
  }

  const yamlString = yamlMatch[1];
  const content = yamlMatch[2];

  // Simple YAML parser for key: value pairs
  const frontmatter: Record<string, any> = {};
  const lines = yamlString.split('\n');
  lines.forEach((line) => {
    const match = line.match(/^([^:]+):\s*(.*)$/);
    if (match) {
      const key = match[1].trim();
      const value = match[2].trim();
      frontmatter[key] = value;
    }
  });

  return { frontmatter: Object.keys(frontmatter).length > 0 ? frontmatter : null, content };
}

export default function JobListPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: jobs } = useJobs();

  // Find selected job from URL param if available
  const selectedJob = jobId && jobs ? jobs.find(j => j.id === jobId) || null : null;

  const [activeTab, setActiveTab] = useState<string | null>('description');
  const [refreshKey, setRefreshKey] = useState(0);
  const [assessmentGeneratingId, setAssessmentGeneratingId] = useState<string | null>(null);
  const [refreshingJobId, setRefreshingJobId] = useState<string | null>(null);
  const [createResumeModalOpen, setCreateResumeModalOpen] = useState(false);
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [editDescriptionModalOpen, setEditDescriptionModalOpen] = useState(false);
  const [markDoneModalOpen, setMarkDoneModalOpen] = useState(false);
  const [markDoneReason, setMarkDoneReason] = useState<string | null>('submitted');
  const [markDoneDate, setMarkDoneDate] = useState<Date | null>(new Date());
  const [markDoneResume, setMarkDoneResume] = useState<string | null>(null);
  const [isGeneratingResume, setIsGeneratingResume] = useState(false);
  const [generatingPlaceholderId, setGeneratingPlaceholderId] = useState<string | null>(null);
  const isDesktop = useMediaQuery('(min-width: 1408px)');

  // Add Job State
  const [showAddModal, setShowAddModal] = useState(false);
  const scrapeJobMutation = useScrapeJob();
  const { searchQuery } = useUIStore();

  const updateJobMutation = useUpdateJob();

  const handleJobSelect = (job: Job) => {
    navigate(`/jobs/${job.id}`);
    setActiveTab('description');
  };

  const handleBack = () => {
    navigate('/jobs');
  };

  // Fetch resume content and job artifacts
  const { data: contentItems = [] } = useResumeContent();
  const { data: jobResumes = [] } = useArtifactsByJob(selectedJob?.id || '', { polling: isGeneratingResume });
  const { data: providers = [] } = useProviders();
  const { data: assessmentStatus } = useGenerationStatus(assessmentGeneratingId) as any;

  // Get most recent resume for submission banner
  const selectedResume = jobResumes
    .filter((a) => a.artifact_type === 'resume')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  // Find the artifact we're currently generating (the placeholder)
  const generatingArtifact = generatingPlaceholderId
    ? jobResumes.find((a) => a.id === generatingPlaceholderId)
    : null;

  // Log polling status and artifact updates
  const pollCountRef = useRef(0);
  useEffect(() => {
    if (isGeneratingResume && generatingPlaceholderId) {
      pollCountRef.current++;
      console.log(`[JobListPage] Poll #${pollCountRef.current} - Resume generation in progress for placeholder:`, generatingPlaceholderId);
      console.log(`[JobListPage] Poll #${pollCountRef.current} - Total resumes: ${jobResumes.length}`);

      if (generatingArtifact) {
        const isPlaceholder = generatingArtifact.content_markdown?.includes('⏳ Generating with AI');
        console.log(`[JobListPage] Poll #${pollCountRef.current} - Generating artifact status:`, {
          id: generatingArtifact.id,
          name: generatingArtifact.name,
          isPlaceholder,
          contentLength: generatingArtifact.content_markdown?.length,
          updated_at: generatingArtifact.updated_at,
        });

        // Stop polling when the placeholder is no longer a placeholder (generation complete)
        if (!isPlaceholder) {
          console.log('[JobListPage] Generation complete, placeholder updated with final content, stopping polling');
          setIsGeneratingResume(false);
          setGeneratingPlaceholderId(null);
        }
      }
    }
  }, [generatingArtifact, isGeneratingResume, generatingPlaceholderId]);

  // Mutations
  const refreshJobMutation = useRefreshJob();
  const assessmentMutation = useGenerateAssessment();

  const handleResumeCreated = () => {
    // Increment refresh key for component re-render
    setRefreshKey((k) => k + 1);

    // Reset generating flag to show the compiler again
    setIsGeneratingResume(false);

    // Force refetch of job artifacts to show newly created resume
    if (selectedJob?.id) {
      queryClient.invalidateQueries({ queryKey: ['artifacts', 'byJob', selectedJob.id] });
    }
  };

  const handleDelete = (job: Job) => {
    // Navigation logic handled in JobList or components if needed
    // Previously this cleared local selectedJob state
    if (jobId === job.id) {
      navigate('/jobs');
    }
  };

  const handleCopyMarkdownToClipboard = () => {
    if (!selectedJob) return;

    navigator.clipboard.writeText(selectedJob.description_markdown).then(() => {
      alert('Job description markdown copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  const handleDownloadMarkdown = () => {
    if (!selectedJob) return;

    const filename = `${selectedJob.title.replace(/\s+/g, '_')}.md`;
    downloadFile(selectedJob.description_markdown, filename, 'text/markdown');
  };

  const handleDownloadJSON = () => {
    if (!selectedJob) return;

    const jsonData = {
      title: selectedJob.title,
      company: selectedJob.company,
      location: selectedJob.location,
      salary: selectedJob.salary,
      deadline: selectedJob.deadline,
      url: selectedJob.url,
      status: selectedJob.status,
      description: selectedJob.description_markdown,
    };

    const filename = `${selectedJob.title.replace(/\s+/g, '_')}.json`;
    downloadFile(JSON.stringify(jsonData, null, 2), filename, 'application/json');
  };

  const handleMarkAsSubmitted = async () => {
    if (!selectedJob) return;
    const isSubmitted = selectedJob.status === 'applied';
    const newStatus = isSubmitted ? 'active' : 'applied';
    try {
      await updateJobMutation.mutateAsync({
        jobId: selectedJob.id,
        updates: { status: newStatus as any },
      });
      // Query invalidation handles update
    } catch (error) {
      console.error('Failed to update job status:', error);
    }
  };

  const handleRefreshJob = async () => {
    if (!selectedJob) return;
    setRefreshingJobId(selectedJob.id);

    try {
      await refreshJobMutation.mutateAsync({
        jobId: selectedJob.id,
        method: 'basic',
      });

      // Query invalidation handles update
      setRefreshingJobId(null);
    } catch (error) {
      console.error('Failed to refresh job:', error);
      setRefreshingJobId(null);
    }
  };

  const handleGenerateAssessment = async () => {
    if (!selectedJob) return;

    const primaryProvider = providers.find((p) => p.is_primary && p.is_active);
    if (!primaryProvider) {
      console.error('No primary LLM provider configured');
      return;
    }

    // Get compiled resume content
    const compiledContent = contentItems
      .filter((item) => item.type !== 'masterTemplate')
      .map((item) => item.content_markdown)
      .join('\n\n---\n\n');

    try {
      const result = await assessmentMutation.mutateAsync({
        jobId: selectedJob.id,
        providerId: primaryProvider.id,
        resumeContent: compiledContent,
      });

      setAssessmentGeneratingId(result.data.id);
    } catch (error) {
      console.error('Failed to generate assessment:', error);
    }
  };

  if (selectedJob) {
    const deadline = selectedJob.deadline ? new Date(selectedJob.deadline).toLocaleDateString() : 'No deadline';

    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Box>
            <PageHeader
              title={selectedJob.title}
              subtitle={selectedJob.company}
              actions={
                <Badge
                  color={statusColors[selectedJob.status]}
                  variant="filled"
                  size="md"
                  tt="uppercase"
                >
                  {statusLabels[selectedJob.status]}
                </Badge>
              }
            />
          </Box>

          {/* Job metadata and action buttons - single bordered container */}
          <Paper p="md" radius="md" withBorder mb="xl">
            <Stack gap="md">
              {/* Job metadata - 2 column table */}
              <Stack gap={0}>
                <Group p="xs" style={{ gap: 0 }}>
                  <Text fw={500} size="sm" c="dimmed" style={{ width: '25%', paddingRight: 'md' }}>
                    Location
                  </Text>
                  <Text size="sm" style={{ width: '75%' }}>
                    {selectedJob.location || 'Not specified'}
                  </Text>
                </Group>
                <Group p="xs" style={{ gap: 0 }}>
                  <Text fw={500} size="sm" c="dimmed" style={{ width: '25%', paddingRight: 'md' }}>
                    Salary
                  </Text>
                  <Text size="sm" style={{ width: '75%' }}>
                    {selectedJob.salary || 'Not specified'}
                  </Text>
                </Group>
                <Group p="xs" style={{ gap: 0 }}>
                  <Text fw={500} size="sm" c="dimmed" style={{ width: '25%', paddingRight: 'md' }}>
                    Source
                  </Text>
                  <Group style={{ width: '75%', gap: 4 }}>
                    <Text component="a" href={selectedJob.url} target="_blank" rel="noopener noreferrer" size="sm" className="view-posting-link" style={{ cursor: 'pointer', transition: 'color 0.2s ease' }}>
                      View Posting
                    </Text>
                    <Text size="sm" c="dimmed">
                      ({new URL(selectedJob.url).hostname.replace('www.', '')})
                    </Text>
                  </Group>
                </Group>
                <Group p="xs" style={{ gap: 0 }}>
                  <Text fw={500} size="sm" c="dimmed" style={{ width: '25%', paddingRight: 'md' }}>
                    Timeline
                  </Text>
                  <Text size="sm" style={{ width: '75%' }}>
                    {(() => {
                      const timelineItems = [];

                      // Added on date
                      timelineItems.push(`Added on ${new Date(selectedJob.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);

                      // Deadline
                      if (selectedJob.deadline) {
                        timelineItems.push(`Deadline is ${new Date(selectedJob.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
                      } else {
                        timelineItems.push('Deadline is unknown');
                      }

                      // Marked status (only show if status is not 'active')
                      if (selectedJob.status !== 'active') {
                        const statusLabels: Record<string, string> = {
                          'applied': 'Marked as Applied',
                          'expired': 'Marked as Expired',
                          'rejected': 'Marked as Rejected',
                          'not-interested': 'Marked as Not Interested',
                          'archived': 'Marked as Archived',
                          'withdrawn': 'Marked as Withdrawn',
                        };
                        const statusLabel = statusLabels[selectedJob.status] || `Marked as ${selectedJob.status}`;

                        // Include date if status_marked_at is available
                        if (selectedJob.status_marked_at) {
                          const markedDate = new Date(selectedJob.status_marked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          timelineItems.push(`${statusLabel} on ${markedDate}`);
                        } else {
                          timelineItems.push(statusLabel);
                        }
                      }

                      return timelineItems.join(' / ');
                    })()}
                  </Text>
                </Group>
              </Stack>

              {/* Action buttons */}
              <Group>
                <Button
                  variant="light"
                  leftSection={<IconCheck size={16} />}
                  onClick={() => setMarkDoneModalOpen(true)}
                >
                  Mark Done
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconFileText size={16} />}
                  onClick={() => setCreateResumeModalOpen(true)}
                >
                  Create Resume
                </Button>
              </Group>
            </Stack>
          </Paper>

          {/* Tabs */}
          {(() => {
            const { frontmatter, content } = extractFrontmatter(selectedJob.description_markdown);
            const tabsData = [
              { value: 'description', label: 'Job Description' },
              { value: 'resumes', label: `Resumes (${jobResumes.length})` },
              { value: 'assessment', label: 'Assessment' },
            ];
            if (frontmatter) {
              tabsData.push({ value: 'details', label: 'Details' });
            }

            const tabsContent = (
              <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List>
                  {tabsData.map((tab) => (
                    <Tabs.Tab key={tab.value} value={tab.value}>
                      {tab.label}
                    </Tabs.Tab>
                  ))}
                </Tabs.List>

                {/* Job Description Tab */}
                <Tabs.Panel value="description" pt="md">
                  <Stack gap="md">
                    <MilkdownRenderer content={content} />

                    {/* Description Controls - 3 columns */}
                    <Group gap="md" grow>
                      <Button
                        variant="light"
                        leftSection={<IconEdit size={16} />}
                        onClick={() => setEditDescriptionModalOpen(true)}
                      >
                        Edit Description
                      </Button>
                      <Button
                        variant="light"
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => setRefreshModalOpen(true)}
                      >
                        Refresh Description
                      </Button>
                      <Menu position="bottom-start" shadow="md">
                        <Menu.Target>
                          <Button variant="light" leftSection={<IconDownload size={16} />} rightSection={<IconChevronDown size={14} />}>
                            Export
                          </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconCopy size={14} />}
                            onClick={handleCopyMarkdownToClipboard}
                          >
                            Copy Markdown to Clipboard
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconFileText size={14} />}
                            onClick={handleDownloadMarkdown}
                          >
                            Download as Markdown
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconBraces size={14} />}
                            onClick={handleDownloadJSON}
                          >
                            Download as JSON
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>

                  </Stack>
                </Tabs.Panel>

                {/* Job Resumes Tab */}
                <Tabs.Panel value="resumes" pt="md">
                  <JobResumesTab
                    key={refreshKey}
                    jobResumes={jobResumes}
                    contentItems={contentItems}
                    jobId={selectedJob?.id || ''}
                    onArtifactRefresh={handleResumeCreated}
                  />
                </Tabs.Panel>

                {/* Assessment Tab */}
                <Tabs.Panel value="assessment" pt="md">
                  <Stack gap="md">
                    <Button
                      variant="light"
                      leftSection={<IconChecklist size={16} />}
                      onClick={() => setAssessmentModalOpen(true)}
                    >
                      Generate Assessment
                    </Button>
                    {assessmentGeneratingId && assessmentStatus && (
                      <Paper p="md" radius="md" withBorder>
                        {assessmentStatus.status === 'pending' && (
                          <Center py="xl">
                            <Loader />
                          </Center>
                        )}
                        {assessmentStatus.status === 'completed' && (
                          <MilkdownRenderer content={assessmentStatus.result || ''} />
                        )}
                        {assessmentStatus.status === 'failed' && (
                          <Alert color="red" title="Generation Failed">
                            {assessmentStatus.error || 'An error occurred while generating the assessment'}
                          </Alert>
                        )}
                      </Paper>
                    )}
                  </Stack>
                </Tabs.Panel>

                {/* Job Details Tab */}
                {frontmatter && (
                  <Tabs.Panel value="details" pt="md">
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {Object.entries(frontmatter).map(([key, value]) => (
                            <tr key={key} style={{ borderBottom: '1px solid var(--mantine-color-gray-6)' }}>
                              <td
                                style={{
                                  padding: '12px',
                                  textAlign: 'left',
                                  fontWeight: 500,
                                  width: '30%',
                                  color: 'var(--mantine-color-gray-4)',
                                }}
                              >
                                {key}
                              </td>
                              <td
                                style={{
                                  padding: '12px',
                                  textAlign: 'left',
                                  width: '70%',
                                }}
                              >
                                {String(value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Tabs.Panel>
                )}
              </Tabs>
            );

            return isDesktop ? (
              <Paper p="md" radius="md" withBorder>
                {tabsContent}
              </Paper>
            ) : (
              tabsContent
            );
          })()}

          {/* Modal for Mark Done */}
          <Modal
            opened={markDoneModalOpen}
            onClose={() => {
              setMarkDoneModalOpen(false);
              setMarkDoneReason(null);
              setMarkDoneDate(new Date());
              setMarkDoneResume(null);
            }}
            title="Mark Job as Done"
            size="md"
          >
            <Stack gap="md">
              <div>
                <Text fw={500} size="sm" mb="xs">Status</Text>
                <div style={{
                  border: '1px solid var(--mantine-color-gray-4)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: 'var(--mantine-color-gray-8)'
                }}>
                  {[
                    { value: 'submitted', label: 'Submitted Application' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'expired', label: 'Expired / Not Available' },
                    { value: 'not-interested', label: 'Not Interested' },
                    { value: 'archived', label: 'Archived' },
                    { value: 'mark-for-deletion', label: 'Mark for Deletion' },
                  ].map((option, index) => (
                    <div
                      key={option.value}
                      onClick={() => setMarkDoneReason(option.value)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: markDoneReason === option.value ? 'var(--mantine-color-blue-7)' : 'transparent',
                        borderBottom: index < 5 ? '1px solid var(--mantine-color-gray-6)' : 'none',
                        transition: 'background-color 0.15s ease, color 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (markDoneReason !== option.value) {
                          e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-7)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (markDoneReason !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <Group gap="xs">
                        <Radio
                          checked={markDoneReason === option.value}
                          onChange={() => setMarkDoneReason(option.value)}
                          value={option.value}
                          label={option.label}
                          styles={{
                            root: { cursor: 'pointer', width: '100%' },
                            label: {
                              color: markDoneReason === option.value ? 'var(--mantine-color-white)' : 'var(--mantine-color-gray-1)',
                              fontWeight: markDoneReason === option.value ? 500 : 400,
                            }
                          }}
                        />
                      </Group>
                    </div>
                  ))}
                </div>
              </div>
              <Box>
                <Text fw={500} size="sm" mb="xs">Date</Text>
                <input
                  type="date"
                  value={markDoneDate ? markDoneDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setMarkDoneDate(e.target.value ? new Date(e.target.value) : null)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--mantine-color-gray-4)',
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                  }}
                />
              </Box>
              {markDoneReason === 'submitted' && (
                <>
                  {(() => {
                    const sortedResumes = jobResumes
                      .filter((a) => a.artifact_type === 'resume')
                      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                    const mostRecentResume = sortedResumes[0];

                    // Auto-select most recent resume on first render
                    if (mostRecentResume && !markDoneResume) {
                      setMarkDoneResume(mostRecentResume.id);
                    }

                    return (
                      <Select
                        label="Resume Version"
                        placeholder="Select resume"
                        data={sortedResumes.map((resume) => ({
                          value: resume.id,
                          label: `${resume.name} (v${resume.version})`,
                        }))}
                        value={markDoneResume}
                        onChange={setMarkDoneResume}
                      />
                    );
                  })()}
                </>
              )}
              <Button
                onClick={async () => {
                  if (!markDoneReason) return;
                  try {
                    // Map modal status values to database status values
                    const statusMap: Record<string, string> = {
                      'submitted': 'applied',
                      'rejected': 'rejected',
                      'expired': 'expired',
                      'not-interested': 'not-interested',
                      'archived': 'archived',
                      'mark-for-deletion': 'archived', // Treat as archived for now
                    };

                    const dbStatus = statusMap[markDoneReason] || markDoneReason;

                    await updateJobMutation.mutateAsync({
                      jobId: selectedJob.id,
                      updates: {
                        status: dbStatus as any,
                        status_marked_at: markDoneDate?.toISOString(),
                      },
                    });
                    setMarkDoneModalOpen(false);
                    setMarkDoneReason(null);
                    setMarkDoneDate(new Date());
                    setMarkDoneResume(null);
                  } catch (error) {
                    console.error('Failed to update job:', error);
                  }
                }}
                disabled={!markDoneReason}
                loading={updateJobMutation.isPending}
              >
                Confirm
              </Button>
            </Stack>
          </Modal>

          {/* Modal for Create Resume */}
          <Modal
            opened={createResumeModalOpen}
            onClose={() => setCreateResumeModalOpen(false)}
            title="Create Resume"
            size="xl"
            scrollAreaComponent={ScrollArea.Autosize}
          >
            {contentItems.length === 0 ? (
              <Stack align="center" py="xl" gap="sm">
                <Text c="dimmed">No resume content available</Text>
                <Text size="sm" c="dimmed">
                  Create content in the Resumes page first
                </Text>
              </Stack>
            ) : isGeneratingResume ? (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <Loader />
                  <Text c="dimmed">Generating AI-tailored resume...</Text>
                  <Text size="sm" c="dimmed" ta="center">
                    This may take a moment. Check the "Job Resumes" tab to see your new resume when it's ready!
                  </Text>
                </Stack>
              </Center>
            ) : (
              <UnifiedArtifactCompiler
                key={refreshKey}
                contentItems={contentItems}
                onArtifactCreated={() => {
                  // Just close the modal - don't stop polling yet
                  // Polling will continue and stop itself when generation completes
                  setCreateResumeModalOpen(false);
                }}
                onGenerationStarted={() => {
                  setIsGeneratingResume(true);
                  // Switch to Job Resumes tab to show placeholder
                  setActiveTab('resumes');
                }}
                onPlaceholderCreated={(placeholderId) => {
                  console.log('[JobListPage] Placeholder created, tracking for polling:', placeholderId);
                  setGeneratingPlaceholderId(placeholderId);
                }}
                preSelectedJobId={selectedJob.id}
                artifactType="resume"
                hideJobSelection={true}
              />
            )}
          </Modal>

          {/* Modal for Assessment */}
          <Modal
            opened={assessmentModalOpen}
            onClose={() => setAssessmentModalOpen(false)}
            title="Generate Assessment"
            size="xl"
            scrollAreaComponent={ScrollArea.Autosize}
          >
            {assessmentStatus?.status === 'processing' || assessmentStatus?.status === 'pending' ? (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <Loader />
                  <Text c="dimmed">Generating job assessment...</Text>
                  <Text size="sm" c="dimmed">
                    Analyzing your resume against this position
                  </Text>
                </Stack>
              </Center>
            ) : assessmentStatus?.status === 'completed' ? (
              <Stack gap="md">
                <Alert icon={<IconCheck size={16} />} color="green">
                  Assessment generated successfully!
                </Alert>
                <Paper p="md" radius="md" bg="gray.0" withBorder>
                  <MilkdownRenderer content={assessmentStatus.result} />
                </Paper>
              </Stack>
            ) : assessmentStatus?.status === 'failed' ? (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {assessmentStatus.error || 'Assessment generation failed. Please try again.'}
              </Alert>
            ) : (
              <Stack gap="md">
                <div>
                  <Text fw={500} mb="sm">
                    Generate Job Assessment
                  </Text>
                  <Text size="sm" c="dimmed" mb="lg">
                    Use AI to analyze how well your resume matches this position and get suggestions for improvement.
                  </Text>
                </div>

                {!providers.some((p) => p.is_primary && p.is_active) && (
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow" mb="md">
                    No primary LLM provider configured. Please set one up in Settings.
                  </Alert>
                )}

                <Button
                  onClick={handleGenerateAssessment}
                  disabled={
                    contentItems.length === 0 ||
                    !providers.some((p) => p.is_primary && p.is_active) ||
                    assessmentMutation.isPending
                  }
                  loading={assessmentMutation.isPending}
                >
                  Generate Assessment with AI
                </Button>
              </Stack>
            )}
          </Modal>

          {/* Modal for Refresh Job */}
          <Modal
            opened={refreshModalOpen}
            onClose={() => setRefreshModalOpen(false)}
            title="Refresh Job Description"
            size="xl"
            scrollAreaComponent={ScrollArea.Autosize}
          >
            {refreshingJobId === selectedJob.id && refreshJobMutation.isPending ? (
              <Center py="xl">
                <Stack align="center" gap="sm">
                  <Loader />
                  <Text c="dimmed">Re-processing job description...</Text>
                </Stack>
              </Center>
            ) : refreshJobMutation.isSuccess ? (
              <Alert icon={<IconCheck size={16} />} color="green" mb="md">
                Job description refreshed successfully!
              </Alert>
            ) : null}

            {refreshJobMutation.isError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
                Failed to refresh job description. Please try again.
              </Alert>
            )}

            <Stack gap="md" align="center" py="xl">
              <Text fw={500} size="lg">Refresh Job Description</Text>
              <Text c="dimmed" ta="center">
                Re-process the job description from the original URL to extract structured metadata and update formatting.
              </Text>
              <Button
                onClick={handleRefreshJob}
                loading={refreshingJobId === selectedJob.id && refreshJobMutation.isPending}
                disabled={refreshingJobId === selectedJob.id && refreshJobMutation.isPending}
                leftSection={<IconRefresh size={16} />}
              >
                Re-Process Job Description
              </Button>
            </Stack>
          </Modal>

          {/* Edit Description Modal */}
          <EditDescriptionModal
            opened={editDescriptionModalOpen}
            onClose={() => setEditDescriptionModalOpen(false)}
            job={selectedJob}
            onSuccess={() => {
              setEditDescriptionModalOpen(false);
              // Refetch the job to get updated description
              if (selectedJob?.id) {
                queryClient.invalidateQueries({ queryKey: ['jobs'] });
              }
            }}
          />

        </Stack>
      </Container>
    );
  }

  if (!selectedJob) {

    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <PageHeader
            title="Job Board"
            subtitle="Manage your job applications."
            actions={
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => setShowAddModal(true)}
              >
                Add Job From URL
              </Button>
            }
          />
          {isDesktop ? (
            <Paper p="md" radius="md" withBorder>
              <JobList onJobSelect={handleJobSelect} />
            </Paper>
          ) : (
            <JobList onJobSelect={handleJobSelect} />
          )}
        </Stack>

        {/* Add Job Modal */}
        <AddJobModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={async (url) => {
            await scrapeJobMutation.mutateAsync(url);
          }}
          isLoading={scrapeJobMutation.isPending}
          error={scrapeJobMutation.error?.message}
          initialUrl={searchQuery}
        />
      </Container>
    );
  }
}
